import { useEffect } from "react";
import {
  X,
  Check,
  Sparkles,
  Crown,
  TrendingUp,
  ExternalLink,
  ShieldCheck,
} from "lucide-react";
import Button from "../components/ui/Button";
import { PLAN_CARDS, PlanCard, Tier } from "./plans";
import env from "../config";

// Marketplace apps choose their plan at INSTALL time — GHL has no in-app plan
// switch. So "changing plans" means reinstalling with the new package. This
// opens THIS app's integration page for the current location, where the user
// uninstalls/reinstalls. Shape: /v2/location/<locationId>/integration/<appId>.
const marketplaceUrl = (locationId: string) =>
  locationId
    ? `https://app.gohighlevel.com/v2/location/${encodeURIComponent(
        locationId,
      )}/integration/${env.GHL.APP_ID}`
    : `https://app.gohighlevel.com/integration/${env.GHL.APP_ID}`;

// The real steps to switch plans on a GHL marketplace app.
const SWITCH_STEPS = [
  "Open the marketplace using the button below — it opens Smart Links in your CRM.",
  "Uninstall Smart Links. This only disconnects the app — your links, QR codes and analytics are kept.",
  "Reinstall it and choose the package you want (Starter or Pro) during install.",
  "Refresh your CRM once. You'll come right back to everything, just as you left it.",
];

interface UpgradeModalProps {
  open: boolean;
  currentTier: Tier;
  locationId: string;
  onClose: () => void;
}

const tierAccent: Record<
  Tier,
  { gradient: string; iconBg: string; icon: any }
> = {
  starter: {
    gradient: "from-amber-50 to-orange-50",
    iconBg:
      "bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-sm",
    icon: TrendingUp,
  },
  pro: {
    gradient: "from-brand-50 to-violet-50",
    iconBg: "bg-brand-gradient text-white shadow-brand-glow",
    icon: Crown,
  },
};

const PlanCardView = ({
  card,
  currentTier,
}: {
  card: PlanCard;
  currentTier: Tier;
}) => {
  const accent = tierAccent[card.tier];
  const Icon = accent.icon;
  const isCurrent = card.tier === currentTier;
  const isPopular = card.tier === "starter";

  return (
    <div
      className={`relative bg-white border rounded-2xl shadow-card overflow-hidden flex flex-col ${
        isCurrent
          ? "border-emerald-300/80 ring-2 ring-emerald-100"
          : isPopular
            ? "border-amber-300/80 ring-2 ring-amber-100"
            : "border-ink-200/70"
      }`}
    >
      {isPopular && !isCurrent && (
        <div className="absolute top-3 right-3">
          <span className="inline-flex items-center gap-1 px-2 h-5 rounded-md text-[10px] font-semibold bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-sm">
            <Sparkles size={10} />
            Most popular
          </span>
        </div>
      )}

      <div
        className={`bg-gradient-to-br ${accent.gradient} px-5 py-4 border-b border-ink-100`}
      >
        <div
          className={`w-9 h-9 rounded-xl flex items-center justify-center ${accent.iconBg}`}
        >
          <Icon size={17} strokeWidth={2.25} />
        </div>
        <h3 className="font-display text-lg font-bold text-ink-900 tracking-tight mt-2.5">
          {card.name}
        </h3>
        <p className="text-xs text-ink-500 mt-0.5">{card.tagline}</p>
      </div>

      <div className="flex-1 px-5 py-4">
        <ul className="space-y-1.5">
          {card.features.map((f, i) => (
            <li
              key={i}
              className={`flex items-start gap-2 text-sm ${
                f.included ? "text-ink-700" : "text-ink-400"
              }`}
            >
              <span
                className={`mt-0.5 shrink-0 w-4 h-4 rounded-full flex items-center justify-center ${
                  f.included
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-ink-100 text-ink-300"
                }`}
              >
                {f.included ? (
                  <Check size={10} strokeWidth={3} />
                ) : (
                  <X size={9} strokeWidth={3} />
                )}
              </span>
              <span className={f.emphasize ? "font-semibold text-ink-900" : ""}>
                {f.label}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {isCurrent && (
        <div className="px-5 pb-4">
          <div className="w-full h-9 rounded-lg bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60 flex items-center justify-center gap-1.5 text-xs font-semibold">
            <Check size={13} /> Your current plan
          </div>
        </div>
      )}
    </div>
  );
};

const UpgradeModal = ({
  open,
  currentTier,
  locationId,
  onClose,
}: UpgradeModalProps) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/50 backdrop-blur-sm p-4 anim-in">
      <div className="bg-ink-50 rounded-3xl shadow-2xl w-full max-w-4xl border border-ink-200/60 flex flex-col max-h-[94vh] overflow-hidden">
        {/* Header */}
        <div className="relative px-6 sm:px-8 pt-5 pb-4 bg-white border-b border-ink-100 shrink-0">
          <div className="absolute inset-0 bg-mesh-1 opacity-50 pointer-events-none" />
          <div className="relative flex items-start justify-between gap-4">
            <div>
              <h2 className="font-display text-xl font-bold text-ink-900 tracking-tight">
                Change your plan
              </h2>
              <p className="text-[13px] text-ink-500 mt-1 max-w-xl">
                Compare what each plan includes, then follow the quick reinstall
                steps below to switch.
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-ink-400 hover:text-ink-700 hover:bg-ink-100 w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body: plan cards + how-to-switch */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-5 sm:px-6 py-5 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {PLAN_CARDS.map((card) => (
                <PlanCardView
                  key={card.tier}
                  card={card}
                  currentTier={currentTier}
                />
              ))}
            </div>

            {/* How to switch — accurate GHL marketplace flow */}
            <div className="bg-white border border-ink-200/70 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-7 h-7 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center">
                  <Sparkles size={14} />
                </span>
                <h3 className="font-display text-sm font-bold text-ink-900">
                  How to switch plans
                </h3>
              </div>
              <ol className="space-y-2.5">
                {SWITCH_STEPS.map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="shrink-0 w-6 h-6 rounded-full bg-brand-50 text-brand-700 ring-1 ring-brand-200/60 flex items-center justify-center text-xs font-semibold">
                      {i + 1}
                    </span>
                    <span className="text-[13px] text-ink-700 leading-relaxed">
                      {step}
                    </span>
                  </li>
                ))}
              </ol>
              <div className="mt-3 flex items-start gap-2 text-[12px] text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200/50 rounded-lg px-3 py-2">
                <ShieldCheck size={14} className="shrink-0 mt-0.5" />
                <span>
                  Your links, QR codes and analytics stay put — reinstalling the
                  same location simply reconnects them.
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer: single action */}
        <div className="px-6 sm:px-8 py-3.5 bg-white border-t border-ink-100 flex items-center justify-between gap-3 flex-wrap shrink-0">
          <span className="text-[11px] text-ink-500">
            Plans are chosen during install in your CRM marketplace.
          </span>
          <a href={marketplaceUrl(locationId)} target="_blank" rel="noreferrer">
            <Button variant="gradient" iconRight={<ExternalLink size={14} />}>
              Open marketplace
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
